const NAME_PARTICLES = new Set(['da', 'das', 'de', 'do', 'dos', 'e'])

const stripAccents = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const words = (value?: string | null): string[] => (value ?? '').trim().split(/\s+/).filter(Boolean)

/** P5: a grade recebe `"<número>-<UF>"`; o card mostra `"CRM-<UF> <número>"`, sem truncar. */
export function formatCrmUf(raw?: string | null): string {
  const value = (raw ?? '').trim()
  if (!value) return ''
  if (/^CRM/i.test(value)) return value
  const separator = value.lastIndexOf('-')
  if (separator <= 0) return `CRM ${value}`
  const uf = value.slice(separator + 1).trim()
  if (!/^[A-Za-z]{2}$/.test(uf)) return `CRM ${value}`
  return `CRM-${uf.toUpperCase()} ${value.slice(0, separator).trim()}`
}

/** P5: primeiro nome + inicial do último sobrenome (partículas ignoradas). */
export function abbreviateDoctorName(fullName?: string | null): string {
  const parts = words(fullName)
  if (!parts.length) return ''
  const rest = parts.slice(1).filter((part) => !NAME_PARTICLES.has(part.toLowerCase()))
  if (!rest.length) return parts[0]
  return `${parts[0]} ${rest[rest.length - 1].charAt(0).toUpperCase()}.`
}

export function nameInitials(fullName?: string | null): string {
  const parts = words(fullName).filter((part) => !NAME_PARTICLES.has(part.toLowerCase()))
  if (!parts.length) return ''
  const first = parts[0].charAt(0)
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''
  return `${first}${last}`.toUpperCase()
}

const HONORIFIC = /^dr(a|ª|\(a\))?\.?$/i

/**
 * QA-11: remove o pronome de tratamento inicial ("Dr.", "Dra.", "Drª", "Dr(a).", com ou sem
 * ponto) para que iniciais e nome abreviado saiam do nome ("Dra. Ana Souza" → "Ana Souza").
 * Usado só nos cards do celular; o desktop segue com o nome como vem da API.
 */
export function stripHonorific(fullName?: string | null): string {
  const parts = words(fullName)
  if (parts.length > 1 && HONORIFIC.test(parts[0])) return parts.slice(1).join(' ')
  return parts.join(' ')
}

/** Sigla curta para tooltip/legenda (setor ou especialidade). */
export function abbreviateLabel(name?: string | null): string {
  const parts = (name ?? '')
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
  if (!parts.length) return ''
  const first = stripAccents(parts[0])
  if (first.length >= 2 && /^[A-Z0-9]+$/.test(first)) return first.slice(0, 4)
  const significant = parts.filter((part) => !NAME_PARTICLES.has(part.toLowerCase()))
  if (significant.length === 1) return stripAccents(significant[0]).slice(0, 3).toUpperCase()
  return significant
    .map((part) => stripAccents(part).charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 4)
}

/** Setor/especialidade arquivados continuam visíveis com sufixo (spec §7). */
export type CatalogRef = { name: string; archivedAt?: string | null; archived?: boolean }

/** Ruling do controller: arquivado = `archivedAt != null || archived === true` (os tipos aceitam ambos). */
export function isRefArchived(
  ref: { archivedAt?: string | null; archived?: boolean } | null | undefined
): boolean {
  return !!ref && (ref.archivedAt != null || ref.archived === true)
}

export function catalogDisplayName(ref?: CatalogRef | null, emptyLabel = ''): string {
  if (!ref) return emptyLabel
  return isRefArchived(ref) ? `${ref.name} (arquivado)` : ref.name
}
